<script>
import DemographicsForm from '../components/globals/DemographicsForm'
import AccountForm from '../components/signup/AccountForm'
import PlansTable from '../components/billing/PlansTable'
import { checkEmail, signup, login } from '../services/userService'

const defaultErrors = {
  firstName: null,
  lastName: null,
  email: null,
  password: null,
  gender: null,
  height: null,
  weight: null
}

export default {
  name: 'RegisterView',
  components: { DemographicsForm, AccountForm, PlansTable },
  data() {
    return {
      step: 0,
      completed: {
        account: false,
        profile: false,
        payment: false
      },
      accountForm: {
        firstName: '',
        lastName: '',
        email: '',
        password: ''
      },
      profileForm: {
        gender: null,
        birthDate: new Date(1990, 1, 1),
        height: null,
        weight: null,
        unitHeight: 'cm',
        unitWeight: 'kg'
      },
      errors: { ...defaultErrors },
      loading: false
    }
  },
  computed: {
    accountFormHasErrors() {
      const { firstName, lastName, email, password } = this.errors
      return _.some([firstName, lastName, email, password])
    }
  },
  watch: {
    step(index) {
      if (index == 3) this.redirect()
    }
  },
  methods: {
    async nextStep() {
      if (!this.validateAccountForm()) return

      this.loading = true

      checkEmail(this.accountForm.email)
        .then(() => {
          this.errors.email = null
          this.completed.account = true
          this.step = 1
        })
        .catch(({ message }) => {
          this.errors.email = message.includes('Email is already taken')
            ? 'Email is already taken'
            : 'You cannot use this email'
          console.log(message)
        })
        .finally(() => (this.loading = false))
    },
    async submit() {
      if (!this.validate()) return

      const newUser = {
        ...this.accountForm,
        ...this.profileForm
      }

      // ToDo: fix NaN issue
      // If weight or height is not set, the NaN value returned
      // And NaN is displayed in the input.
      newUser.weight = parseFloat(newUser.weight)
      newUser.height = parseFloat(newUser.height)

      this.loading = true

      try {
        if (await signup(newUser)) {
          this.identify(newUser)

          this.$amplitude.logEvent('User Created', {
            first_name: newUser.firstName,
            last_name: newUser.lastName,
            email: newUser.email,
            birth_date: newUser.birthDate,
            gender: newUser.gender
          })

          const { email, password } = newUser
          login({ email, password })
            .then(loginData => {
              this.$store.dispatch('login', loginData)
              this.completed.profile = true

              this.$route.params.plan === 'biohacker'
                ? (this.step = 2)
                : this.redirect()
            })
            .finally(() => (this.loading = false))
        }
      } catch ({ message }) {
        if (message.includes('Error, expected `email` to be unique')) {
          this.errors.email = 'Email is already taken'
          this.step = 0
        }
        this.loading = false
      }
    },
    redirect() {
      this.$router.replace({ name: 'DashboardView' })
    },
    onSubscribe() {
      this.completed.payment = true
      this.redirect()
    },
    validate() {
      this.errors = { ...defaultErrors }

      if (!this.profileForm.gender)
        return !(this.errors.gender = 'Select your gender.')
      if (!this.profileForm.height)
        return !(this.errors.height = 'Enter your height.')
      if (!this.profileForm.weight)
        return !(this.errors.weight = 'Enter your weight.')

      return this.validateAccountForm()
    },
    validateAccountForm() {
      this.errors = { ...defaultErrors }

      if (!this.accountForm.firstName)
        return !(this.errors.firstName = 'Enter your first name.')
      if (!this.accountForm.lastName)
        return !(this.errors.lastName = 'Enter your last name.')
      if (!this.accountForm.email)
        return !(this.errors.email = 'Enter your email.')
      if (!this.accountForm.password)
        return !(this.errors.password = 'Choose your password.')
      if (this.accountForm.password.length < 6)
        return !(this.errors.password =
          'Your password must have at least 6 characters.')

      return true
    },
    scrollToBottom() {
      this.$nextTick(() =>
        window.scrollTo({
          top: document.body.scrollHeight,
          left: 0,
          behavior: 'smooth'
        })
      )
    },
    identify(user) {
      this.$amplitude.setUserId(user._id)

      const identify = new this.$amplitude.Identify()
        .set('email', user.email)
        .set('first_name', user.firstName)
        .set('last_name', user.lastName)
        .set('birth_date', user.birthDate)
        .set('gender', user.gender)
        .setOnce('created_at', new Date())
        .add('logins', 1)

      this.$amplitude.identify(identify)
    }
  }
}
</script>

<template>
  <section class="signup-view section container">
    <div class="columns is-vertical-center">
      <div class="container column is-four-fifths">
        <!-- <div
          :class="{
            'hero-block': true,
            'is-hidden': completed.profile
          }"
        >-->
        <div class="illustration is-hidden-mobile">
          <img src="@/assets/img/illustrations/sign-up-3.png" alt="Login" />
        </div>
        <h2 class="title is-3 has-text-centered is-spaced">
          Take charge of your health
        </h2>
        <div class="subtitle has-text-centered is-spaced">
          Already a Plas.<i>me</i> user?
          <router-link :to="{ name: 'LoginView' }">Log in here</router-link>
        </div>
        <!-- </div> -->

        <div>
          <hr class="spacer" />
          <b-steps
            v-model="step"
            size="is-small"
            type="is-secondary"
            :animated="false"
            :has-navigation="false"
            @change="scrollToBottom"
          >
            <b-step-item
              label="Account"
              :completed="completed.account"
              :clickable="false"
            >
              <form class="container" @submit.prevent="nextStep">
                <AccountForm
                  :title="false"
                  :user-form="accountForm"
                  :errors="errors"
                />

                <div class="spacer" />

                <article class="message is-info">
                  <div class="message-body">
                    By clicking Continue you agree to the
                    <a href="https://www.plas.me/tos" target="_blank"
                      >terms of service, cookie policy and privacy policy</a
                    >
                  </div>
                </article>

                <div class="is-clearfix">
                  <b-button
                    :type="completed.profile ? '' : 'is-secondary'"
                    native-type="submit"
                    class="is-pulled-right"
                    rounded
                    :disabled="completed.profile"
                    :loading="loading"
                    >Continue</b-button
                  >
                </div>
              </form>
            </b-step-item>

            <b-step-item
              label="Profile"
              :completed="completed.profile"
              :clickable="false"
            >
              <form class="container" @submit.prevent="submit">
                <div>
                  <DemographicsForm
                    :title="false"
                    :user-form="profileForm"
                    :errors="errors"
                    autofocus
                  />
                </div>
                <br />

                <b-button
                  v-if="$route.params.plan === 'biohacker'"
                  :type="completed.profile ? '' : 'is-secondary'"
                  native-type="submit"
                  class="is-pulled-right"
                  rounded
                  :disabled="completed.profile"
                  :loading="loading"
                  >Continue</b-button
                >

                <b-button
                  v-else
                  :type="completed.profile ? '' : 'is-secondary'"
                  native-type="submit"
                  class="is-pulled-right"
                  rounded
                  :disabled="completed.profile"
                  :loading="loading"
                  >Join Plas.<i>me</i></b-button
                >
              </form>
            </b-step-item>

            <b-step-item
              :visible="$route.params.plan === 'biohacker'"
              label="Payment"
              :completed="completed.payment"
              :clickable="false"
            >
              <PlansTable
                v-if="completed.profile"
                :show-free-plan="false"
                :show-features="false"
                @subscribe="onSubscribe"
              />
              <hr class="spacer" />
              <div class="has-text-centered has-text-success">
                <b-icon pack="fas" icon="shield-alt" size="is-small" />
                <p class="subtitle is-spaced is-5 has-text-success">
                  Secure payment form
                </p>
              </div>
            </b-step-item>

            <b-step-item label="Done" :clickable="false">
              <!-- This step is only for quantity. Two-step form looks bad -->
            </b-step-item>
          </b-steps>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="sass">
.signup-view
  .b-steps .steps
    max-width: 490px
    margin: 0 auto
  .field
    &.is-expanded
      margin-bottom: 0
  .title
    margin: 0.5rem 0
  .illustration
    padding: 0 0 25px
    text-align: center
    img
      max-height: 200px
  .step-marker
    transition: 0.3s
  .step-item > .container
    max-width: 440px
    margin: 0 auto
  .step-link
    &:hover
      color: #4a4a4a !important
    &:not(.is-clickable)
      cursor: initial !important
</style>
